import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiPackage,
  FiSearch,
  FiFilter,
  FiInfo,
  FiCheckCircle,
  FiXCircle,
  FiPlus,
  FiArrowRight,
  FiTag,
  FiBox,
  FiChevronRight,
  FiFileText,
  FiArrowLeft,
  FiLayers,
} from "react-icons/fi";
import toast from "react-hot-toast";
import axios from "../../Utils/axiosInstance";
import AppLoader from "../../Components/Common/AppLoader";
import { useAuth } from "../../Components/Context/AuthContext";

const STATUS_TABS = [
  { key: "ALL", label: "All" },
  { key: "ACTIVE", label: "Active" },
  { key: "INACTIVE", label: "Inactive" },
];

const SupplierLinkedVarieties = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const targetIdParam = searchParams.get("varietyId");

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const fetchLinkedVarieties = useCallback(async () => {
    const supplierId = user?._id || user?.id;
    if (!supplierId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(
        `/product-variety-supplier/get-all?supplierId=${supplierId}&populate=true`
      );
      if (res?.data?.success) {
        const dataList = res.data.data || [];
        setLinks(dataList);
      } else {
        toast.error("Failed to load linked product varieties");
      }
    } catch (err) {
      console.error("Fetch supplier linked varieties error:", err);
      toast.error(err?.response?.data?.message || "Failed to fetch linked product varieties");
    } finally {
      setLoading(false);
    }
  }, [user, targetIdParam]);

  useEffect(() => {
    fetchLinkedVarieties();
  }, [fetchLinkedVarieties]);

  // Statistics
  const stats = useMemo(() => {
    const total = links.length;
    const activeCount = links.filter((l) => l.isActive && l.variety?.isActive !== false).length;
    const categoriesSet = new Set(
      links
        .map((l) => l.category?.name || l.category?._id)
        .filter(Boolean)
    );
    return {
      total,
      activeCount,
      categoriesCount: categoriesSet.size,
    };
  }, [links]);

  // Filtered & Sorted links
  const filteredLinks = useMemo(() => {
    return links
      .filter((item) => {
        // Tab Filter
        if (activeTab === "ACTIVE" && (!item.isActive || item.variety?.isActive === false)) {
          return false;
        }
        if (activeTab === "INACTIVE" && item.isActive && item.variety?.isActive !== false) {
          return false;
        }

        // Search Query Filter
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        const vName = (item.variety?.varietyName || "").toLowerCase();
        const pName = (item.product?.name || "").toLowerCase();
        const cName = (item.category?.name || "").toLowerCase();
        const scName = (item.subCategory?.name || "").toLowerCase();
        const grade = (item.variety?.grade || "").toLowerCase();
        const hsCode = (item.product?.hsCode || "").toLowerCase();

        return (
          vName.includes(query) ||
          pName.includes(query) ||
          cName.includes(query) ||
          scName.includes(query) ||
          grade.includes(query) ||
          hsCode.includes(query)
        );
      })
      .sort((a, b) => {
        if (sortBy === "name_asc") {
          return (a.variety?.varietyName || "").localeCompare(b.variety?.varietyName || "");
        }
        if (sortBy === "name_desc") {
          return (b.variety?.varietyName || "").localeCompare(a.variety?.varietyName || "");
        }
        if (sortBy === "moq_low") {
          return (a.MOQ_For_Variety || 0) - (b.MOQ_For_Variety || 0);
        }
        if (sortBy === "moq_high") {
          return (b.MOQ_For_Variety || 0) - (a.MOQ_For_Variety || 0);
        }
        // Default newest
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      });
  }, [links, activeTab, searchQuery, sortBy]);

  const handlePostRQM = (item) => {
    const vId = item.variety?._id;
    const pId = item.product?._id;
    const scId = item.subCategory?._id;
    const cId = item.category?._id;

    if (vId) {
      navigate(`/post-rqm-request?variety=${vId}&product=${pId || ""}&subCategory=${scId || ""}&category=${cId || ""}`);
    } else {
      toast.error("Variety details incomplete for posting RQM");
    }
  };

  if (loading) {
    return <AppLoader fullScreen message="Loading your linked products and varieties..." />;
  }

  return (
    <div className="w-full bg-[#F8FAFC] min-h-screen py-6 px-4 md:px-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* HEADER BAR */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 md:p-6 rounded-2xl border border-gray-200 shadow-2xs">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primaryColor/10 text-primaryColor text-xs font-bold mb-2">
              <FiPackage size={14} /> Linked Product Varieties
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
              My Linked Products & Varieties
            </h1>
            <p className="text-xs md:text-sm text-gray-500 mt-1 max-w-3xl leading-relaxed">
              Explore product varieties linked to your supplier profile. View assigned MOQs, standardized units, and post RQM requests directly.
            </p>
          </div>

          {/* <button
            onClick={() => navigate("/post-rqm-request")}
            className="flex items-center justify-center gap-2 bg-primaryColor hover:bg-primaryColor/90 text-white font-semibold text-xs md:text-sm px-5 py-3 rounded-xl shadow-md transition duration-200 active:scale-95 shrink-0"
          >
            <FiPlus size={18} /> Post New RQM Request
          </button> */}
        </div>

        {/* METRICS SUMMARY CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-2xs flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Total Linked Varieties</span>
              <p className="text-2xl font-extrabold text-gray-900 mt-1">{stats.total}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-primaryColor/10 text-primaryColor flex items-center justify-center">
              <FiPackage size={24} />
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-2xs flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Active Varieties</span>
              <p className="text-2xl font-extrabold text-emerald-600 mt-1">{stats.activeCount}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <FiCheckCircle size={24} />
            </div>
          </div>

          <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-2xs flex items-center justify-between">
            <div>
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Categories Covered</span>
              <p className="text-2xl font-extrabold text-blue-600 mt-1">{stats.categoriesCount}</p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
              <FiLayers size={24} />
            </div>
          </div>
        </div>

        {/* SEARCH AND CONTROLS */}
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-200 shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* SEARCH BAR */}
          <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 focus-within:border-primaryColor focus-within:ring-2 focus-within:ring-primaryColor/10 transition flex-1 max-w-md">
            <FiSearch className="text-gray-400 mr-2 flex-shrink-0" size={18} />
            <input
              type="text"
              placeholder="Search by variety, product, grade, category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent outline-none text-xs sm:text-sm w-full text-gray-800 placeholder-gray-400 font-medium"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="text-xs text-gray-400 hover:text-gray-600 font-bold ml-1"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* STATUS TABS */}
            <div className="flex items-center gap-1.5 bg-gray-100 p-1 rounded-xl">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${activeTab === tab.key
                    ? "bg-white text-primaryColor shadow-2xs"
                    : "text-gray-600 hover:text-gray-900"
                    }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* SORT DROPDOWN */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-gray-500 hidden sm:inline">Sort:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-gray-50 border border-gray-200 text-gray-700 text-xs font-bold rounded-xl px-3 py-2 outline-none focus:border-primaryColor"
              >
                <option value="newest">Newest First</option>
                <option value="name_asc">Variety Name (A-Z)</option>
                <option value="name_desc">Variety Name (Z-A)</option>
                <option value="moq_low">Supplier MOQ (Low to High)</option>
                <option value="moq_high">Supplier MOQ (High to Low)</option>
              </select>
            </div>
          </div>
        </div>

        {/* VARIETIES GRID */}
        {filteredLinks.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-12 text-center space-y-4">
            <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
              <FiPackage size={32} />
            </div>
            <h3 className="text-base font-bold text-gray-800">No Linked Product Varieties Found</h3>
            <p className="text-xs sm:text-sm text-gray-500 max-w-md mx-auto">
              {searchQuery
                ? "No product varieties match your search criteria. Try modifying your search term."
                : "You currently have no product varieties linked to your supplier profile."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredLinks.map((item) => {
              const variety = item.variety || {};
              const product = item.product || {};
              const category = item.category || {};
              const subCategory = item.subCategory || {};
              const isLinkActive = item.isActive && variety.isActive !== false;
              const unit = variety.standardizedUNIT || "Kg";
              const supplierMoq = item.MOQ_For_Variety || variety.slabAdjustedMOQ || variety.minimum_MOQ || 0;

              return (
                <div
                  key={item._id}
                  className="bg-white border border-gray-200 hover:border-primaryColor/40 rounded-2xl p-5 shadow-2xs hover:shadow-md transition-all duration-200 flex flex-col justify-between space-y-4 group relative"
                >
                  {/* CARD TOP HEADER */}
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      {/* IMAGE THUMBNAIL */}
                      <div className="w-16 h-16 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex-shrink-0 flex items-center justify-center p-1">
                        {variety.img ? (
                          <img
                            src={variety.img}
                            alt={variety.varietyName}
                            className="w-full h-full object-cover rounded-lg group-hover:scale-105 transition duration-200"
                          />
                        ) : (
                          <FiBox size={28} className="text-gray-400" />
                        )}
                      </div>

                      <div className="flex flex-col items-end gap-1.5">
                        <span
                          className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border ${isLinkActive
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-red-50 text-red-700 border-red-200"
                            }`}
                        >
                          {isLinkActive ? "Active" : "Inactive"}
                        </span>
                        {variety.grade && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                            Grade: {variety.grade}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* CATEGORY & VARIETY NAMES */}
                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wide truncate">
                        {category.name || "Category"} {subCategory.name ? `> ${subCategory.name}` : ""}
                      </div>
                      <h3 className="text-base font-extrabold text-gray-900 group-hover:text-primaryColor transition line-clamp-1">
                        {variety.varietyName || "Variety Name"}
                      </h3>
                      <p className="text-xs text-gray-600 font-semibold line-clamp-1">
                        Product: {product.name || "Product Name"}
                        {product.hsCode ? ` (HS: ${product.hsCode})` : ""}
                      </p>
                    </div>
                  </div>

                  {/* SPECIFICATION PILLS */}
                  <div className="pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-primaryColor/5 p-2.5 rounded-xl border border-primaryColor/10">
                      <span className="text-[10px] font-bold uppercase text-primaryColor block">Supplier MOQ</span>
                      <span className="font-extrabold text-gray-900 text-sm block mt-0.5">
                        {supplierMoq} {unit}
                      </span>
                    </div>

                    <div className="bg-gray-50 p-2.5 rounded-xl border border-gray-200/80">
                      <span className="text-[10px] font-bold uppercase text-gray-500 block">Standard Unit</span>
                      <span className="font-extrabold text-gray-800 text-sm block mt-0.5">
                        {unit}
                      </span>
                    </div>
                  </div>

                  {/* NOTES REMARKS IF ANY */}
                  {item.notes && (
                    <div className="text-[11px] text-gray-500 bg-amber-50/70 border border-amber-200/60 p-2.5 rounded-xl flex items-start gap-2">
                      <FiInfo size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <span className="line-clamp-2">{item.notes}</span>
                    </div>
                  )}

                  {/* CARD BOTTOM ACTION BUTTONS */}
                  <div className="pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handlePostRQM(item)}
                      className="w-full py-2.5 px-3 bg-primaryColor hover:bg-primaryColor/90 text-white font-bold text-xs rounded-xl shadow-2xs transition flex items-center justify-center gap-1.5"
                    >
                      <FiPlus size={16} /> Post RQM Request
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierLinkedVarieties;
